import { useEffect } from 'react';
import { verifyConnection } from '@/core/api/supabase';
import { useConnectionStore } from '../stores/connection-store';

/**
 * Hook that continually verifies connectivity to Supabase.
 * It
 *  – polls `verifyConnection()` every `pollMs` (default 30 s)
 *  – updates a Zustand store so any component can react
 *  – listens to browser online/offline events for instant feedback
 */
export function useSupabaseHealth(pollMs: number = 30_000): void {
  const setState = useConnectionStore((s) => s.setState);

  useEffect(() => {
    let cancelled = false;

    const update = async () => {
      const ok = await verifyConnection();
      if (!cancelled) {
        setState({ online: ok, lastCheck: Date.now() });
      }
    };

    // initial check and start interval
    update();
    const id = setInterval(update, pollMs);

    // browser events for faster updates
    const onlineHandler = () => update();
    const offlineHandler = () => setState({ online: false, lastCheck: Date.now() });

    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  }, [pollMs, setState]);
}
